/**
 * CORE-AW — capacity that says which of the three things it is.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE DEFECT THIS CLOSES
 * ═══════════════════════════════════════════════════════════════════════════
 * `calculateTechCapacity` returned a bare `number`, and returned `0` for a
 * technician whose hours nobody had ever recorded. Zero is a number, so every
 * consumer treated it as a measurement: the Dashboard card printed "Off
 * today", the OBLI bucket dropped the person from "techs working on this
 * date", and four aggregates added their zero into a total presented as the
 * shop's capacity for the day.
 *
 * None of that was true. It is the assumption CORE-AW §44 forbids, in the
 * direction people forget to guard:
 *
 *   "Where a technician is not scheduled, capacity SHALL be zero — and where
 *    their working hours are unknown, capacity MUST NOT be assumed. Unknown is
 *    not a full day."
 *
 * Unknown is not a full day, and it is not an empty one either. The server
 * stopped conflating them (`_shared/technicianScheduleState.ts`); until now
 * the browser still did, so the same question got two answers depending on
 * which half of the product asked it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS IS
 * ═══════════════════════════════════════════════════════════════════════════
 * The schedule-state doctrine decides WHICH state a technician is in; this
 * module decides what capacity follows from it, and nothing else. It invents
 * no fourth state: every value here comes from `resolveTechnicianScheduleState`
 * and maps one-to-one onto its three.
 *
 * Capacity remains ADVISORY. Nothing here refuses an assignment, hides a
 * technician, or gates an action — an unknown is surfaced, never enforced.
 */
import { planningBufferFraction } from '@/utils/planningBuffer';
import { format } from 'date-fns';
import {
  resolveTechnicianScheduleState,
  describeScheduleState,
  type TechnicianScheduleState,
} from './technicianScheduleState';

interface Break { start: string; end: string; name: string }

/**
 * What is known about one technician's capacity on one date.
 *
 * `known_zero` and `unknown` are deliberately NOT both "0 minutes". A caller
 * that wants the old behaviour has to ask for it by name, which is the whole
 * point — the previous shape made the wrong reading the default one.
 */
export type TechnicianCapacity =
  /** Scheduled, with computable bounds. `minutes` is the capacity. */
  | { state: 'known'; minutes: number; schedule: TechnicianScheduleState }
  /** Not scheduled. Zero capacity, and that zero is a fact about the day. */
  | { state: 'known_zero'; minutes: 0; schedule: TechnicianScheduleState }
  /** Hours unknown. There is no number, and none may be substituted. */
  | { state: 'unknown'; schedule: TechnicianScheduleState };

const timeToMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const breakOverlap = (bs: number, be: number, ss: number, se: number): number =>
  Math.max(0, Math.min(be, se) - Math.max(bs, ss));

/**
 * The minutes arithmetic, unchanged from the original helper: shift bounds,
 * minus recurring breaks that overlap the shift, times efficiency, less
 * buffer. Only the SHAPE around it changed.
 */
/**
 * Which capacity a caller is asking for.
 *
 * - `adjusted` (default, unchanged): scheduled minutes × efficiency × (1 − buffer)
 *   — the technician's capacity expressed in BOOK minutes, for callers that
 *   compare it with book time.
 * - `scheduled`: the scheduled working minutes themselves (shift minus breaks),
 *   with no efficiency and no buffer. The load index compares capacity with
 *   OptiTime workload, which ALREADY carries efficiency (book ÷ efficiency) and
 *   buffer (× (1 + buffer)); applying either again to capacity counted it twice.
 */
export type CapacityBasis = 'adjusted' | 'scheduled';

/**
 * Core-100 R4 (CORE-AW): "Subtract only the breaks, absence intervals and exceptions that genuinely overlap the
 * window." The browser capacity used to subtract recurring breaks only — a partial-day absence (manager-recorded
 * in technician_absence_intervals) and a break dated for this day were ignored, so the board overstated capacity.
 * Every unavailable interval for THIS day is now merged (a union, so overlaps are not counted twice) and the part
 * inside the shift is subtracted. `tech.absence_intervals` is supplied by callers that loaded them
 * (useTechnicianAbsenceIntervals); without it the result is what it always was.
 */
export function unavailableMinutesInShift(
  tech: any, dateLabel: string, shiftStartMin: number, shiftEndMin: number,
): number {
  const spans: Array<[number, number]> = [];
  const add = (s: string | null | undefined, e: string | null | undefined) => {
    if (!s || !e) return;
    const a = Math.max(timeToMinutes(s), shiftStartMin);
    const b = Math.min(timeToMinutes(e), shiftEndMin);
    if (b > a) spans.push([a, b]);
  };
  for (const brk of ((tech?.recurring_breaks as Break[]) || [])) add(brk.start, brk.end);
  for (const brk of ((tech?.non_recurring_breaks as Array<Break & { date?: string }>) || [])) {
    if (brk.date === dateLabel) add(brk.start, brk.end);
  }
  for (const ab of ((tech?.absence_intervals as Array<{ absence_date: string; start_time: string; end_time: string }>) || [])) {
    if (String(ab.absence_date).slice(0, 10) === dateLabel) add(ab.start_time, ab.end_time);
  }
  spans.sort((x, y) => x[0] - y[0]);
  let total = 0; let curS = -1; let curE = -1;
  for (const [s, e] of spans) {
    if (s > curE) { if (curE > curS) total += curE - curS; curS = s; curE = e; }
    else curE = Math.max(curE, e);
  }
  if (curE > curS) total += curE - curS;
  return total;
}

function computeMinutes(tech: any, open: string, close: string, basis: CapacityBasis = 'adjusted', dateLabel = ''): number {
  const shiftStartMin = timeToMinutes(open);
  const shiftEndMin = timeToMinutes(close);
  if (shiftEndMin <= shiftStartMin) return 0;

  const availableMinutes = Math.max(0, (shiftEndMin - shiftStartMin) - unavailableMinutesInShift(tech, dateLabel, shiftStartMin, shiftEndMin));
  if (basis === 'scheduled') return availableMinutes;

  const efficiency = (tech.efficiency_percentage || 100) / 100;
  const buffer = planningBufferFraction(tech.buffer_percentage);
  return Math.max(0, Math.round(availableMinutes * efficiency * (1 - buffer)));
}

/**
 * Capacity for one technician on one shop-local date.
 *
 * Core-100 R4 W5 (CORE-AW Rel): `date` may be the shop-local day LABEL (YYYY-MM-DD) — taken as is. A `Date` is keyed
 * by its calendar date in the device's zone (`format`), which is right only for a calendar cell built in the
 * device's zone; a surface that knows the shop's day (getTodayInShopTz) passes the label, so a device in another zone
 * — or a DST night — never reads the neighbouring day's schedule.
 * (CORE-AW T): a day whose close is not after its open (an overnight entry this model cannot measure) is UNKNOWN,
 * not a known zero; before the first day of employment is a known zero — the same answers as the server's
 * `computeTechDayCapacity` (parity-tested).
 */
export function resolveTechnicianCapacity(tech: any, date: Date | string, basis: CapacityBasis = 'adjusted'): TechnicianCapacity {
  const label = typeof date === 'string' ? date.slice(0, 10) : format(date, 'yyyy-MM-dd');
  const schedule = resolveTechnicianScheduleState(
    { hours: tech?.hours ?? null, time_off: tech?.time_off ?? [], hire_date: tech?.hire_date ?? null },
    label,
  );

  switch (schedule.state) {
    case 'scheduled':
      if (timeToMinutes(schedule.close) <= timeToMinutes(schedule.open)) return { state: 'unknown', schedule };
      return {
        state: 'known',
        minutes: computeMinutes(tech, schedule.open, schedule.close, basis, label),
        schedule,
      };
    case 'not_scheduled':
      return { state: 'known_zero', minutes: 0, schedule };
    case 'hours_unknown':
      return { state: 'unknown', schedule };
  }
}

/** Operator-facing sentence. Never says "off" about an unknown. */
export function describeCapacity(c: TechnicianCapacity): string {
  return describeScheduleState(c.schedule);
}

/** True only where the technician genuinely has no capacity today. */
export const isEstablishedZero = (c: TechnicianCapacity): boolean =>
  c.state === 'known_zero';

/**
 * True where the person is known NOT to be working — and false for an
 * unknown, which is why every "off today" reading has to go through here.
 */
export const isKnownOffToday = (c: TechnicianCapacity): boolean =>
  c.state === 'known_zero';

// ═══════════════════════════════════════════════════════════════════════════
// AGGREGATES — a total that says whether it is complete
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The sum of what is known, plus how much is not known.
 *
 * `minutes` deliberately excludes unknown technicians rather than counting
 * them as zero: including them would understate the shop and present the
 * result as a complete measurement. `complete` is the flag every consumer
 * must consult before describing this number as the shop's capacity.
 */
export interface ShopCapacity {
  /** Sum over technicians whose capacity is KNOWN (scheduled + not scheduled). */
  minutes: number;
  /** Technicians contributing a known number (including established zeroes). */
  knownCount: number;
  /** Technicians whose hours are not recorded, so they contribute nothing. */
  unknownCount: number;
  /** False when any technician's capacity is unknown. */
  complete: boolean;
  /**
   * Technicians deliberately left out because they do not perform MECHANICAL
   * work. Distinct from `unknownCount`: their hours are known, they are just
   * not part of this question. Zero when the caller did not say who performs
   * what, which is the legacy answer.
   */
  nonMechanicalCount?: number;
}

export function aggregateShopCapacity(
  techs: any[],
  date: Date | string,
  /**
   * The technicians whose hours are MECHANICAL capacity, when the caller
   * knows. Omit — or pass `null` — and every technician counts, which is the
   * pre-multi-role behaviour and the right answer when nobody has said who
   * does what.
   *
   * ── WHY THIS EXISTS ────────────────────────────────────────────────────
   * This total is divided into booked MECHANICAL minutes and presented as
   * shop utilization. A detail specialist's eight hours in that denominator
   * make the shop look half as busy as it is, and the error is invisible:
   * the number is plausible, it moves in the right direction, and the only
   * symptom is a manager who thinks there is room for another job.
   *
   * Detail hours are real hours. They are not mechanical capacity, and this
   * ratio is about mechanical work.
   */
  mechanicalTechnicianIds?: ReadonlySet<string> | null,
  /** See `CapacityBasis`; the load index asks for `scheduled`. */
  basis: CapacityBasis = 'adjusted',
): ShopCapacity {
  let minutes = 0, knownCount = 0, unknownCount = 0, excludedCount = 0;
  for (const tech of (techs || [])) {
    if (mechanicalTechnicianIds && !mechanicalTechnicianIds.has(String(tech?.id))) {
      // Not counted, and not counted as UNKNOWN either. Their capacity is
      // perfectly well known; it simply belongs to a different question, and
      // marking the total incomplete because of them would attach a caveat
      // to an answer that does not need one.
      excludedCount += 1;
      continue;
    }
    const c = resolveTechnicianCapacity(tech, date, basis);
    if (c.state === 'unknown') { unknownCount += 1; continue; }
    minutes += c.minutes;
    knownCount += 1;
  }
  const base: ShopCapacity = {
    minutes, knownCount, unknownCount, complete: unknownCount === 0,
  };

  // The key is ADDED only when a filter was actually applied, so a caller that
  // passes no filter gets a byte-identical result to the one it got before
  // multi-role existed. `coreAwBrowserCapacityTruth` compares this object with
  // `toEqual`, and rightly: the shape of a capacity answer is part of its
  // contract, and quietly growing a field is how a deep-equality guard starts
  // failing for a reason that has nothing to do with capacity truth.
  return mechanicalTechnicianIds
    ? { ...base, nonMechanicalCount: excludedCount }
    : base;
}

/**
 * A utilization percentage, or `null` where the denominator cannot support
 * one.
 *
 * `null` — not 0 — is the answer when capacity is unknown or zero. A
 * percentage of an unknown denominator is not a small number; it is not a
 * number, and rendering it as 0% states that the person is idle when nothing
 * of the sort has been established.
 */
export function capacityUtilizationPercent(
  assignedMinutes: number,
  capacity: TechnicianCapacity,
): number | null {
  if (capacity.state !== 'known' || capacity.minutes <= 0) return null;
  return Math.min(100, Math.round((assignedMinutes / capacity.minutes) * 100));
}

/**
 * The shop-level equivalent. Computed over KNOWN capacity only, and the
 * caller is told so by `ShopCapacity.complete` — a ratio against a partial
 * denominator is honest arithmetic only while it is labelled as partial.
 */
export function shopUtilizationPercent(
  bookedMinutes: number,
  shop: ShopCapacity,
): number | null {
  if (shop.minutes <= 0) return null;
  return Math.round((bookedMinutes / shop.minutes) * 100);
}

/**
 * A short qualifier for any surface presenting a capacity total, or `null`
 * when the total is complete and needs none.
 */
export function describeCapacityCompleteness(shop: ShopCapacity): string | null {
  if (shop.complete) return null;
  const n = shop.unknownCount;
  return `Excludes ${n} technician${n === 1 ? '' : 's'} whose working hours are not set`;
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPACITY-DEPENDENT METRICS — what may be claimed, and what may not
// ═══════════════════════════════════════════════════════════════════════════

/** The measured inputs a productivity window produces. */
export interface CapacityWindowInput {
  /** Sum of KNOWN capacity across the window. Unknown days contribute none. */
  availableMinutes: number;
  /** Observed. */
  wrenchingMinutes: number;
  /** Observed. */
  bookedMinutes: number;
  /** Observed. */
  pauseMinutes: number;
  /** Technician-days whose capacity could not be established. */
  unknownCapacityDays: number;
}

export interface CapacityWindowMetrics {
  /** Wrenching ÷ available, or null where no capacity is known. */
  utilizationPct: number | null;
  /** Booked ÷ available, or null on the same terms. */
  bookLoadPct: number | null;
  /** Available minus wrenching minus pause, or null on the same terms. */
  idleMinutes: number | null;
  /** Observed, and never capacity-dependent. */
  overUnderMinutes: number;
  /** False where any contributing day's capacity is unknown. */
  capacityComplete: boolean;
  /** True where NO capacity could be established at all. */
  capacityUnknown: boolean;
}

/**
 * Derive the capacity-dependent figures, refusing the ones that cannot be
 * supported.
 *
 * The defect this replaces was one ternary: `availableMinutes > 0 ? … : 0`.
 * `availableMinutes` is 0 precisely when nothing is known about the day
 * length, so the fallback published a measurement — "0% utilised", "0 minutes
 * idle" — from the absence of the only input those figures need.
 *
 * Three cases, deliberately distinct:
 *
 *   some known capacity      compute. If any day was unknown the figure is
 *                            over a PARTIAL denominator, `capacityComplete`
 *                            is false and the surface must disclose it.
 *   no capacity, none known  null. There is no number to give.
 *   no capacity, known zero  0, exactly as before — an established zero is a
 *                            fact about the window, not a gap in it.
 */
export function deriveCapacityWindowMetrics(
  input: CapacityWindowInput,
): CapacityWindowMetrics {
  const {
    availableMinutes, wrenchingMinutes, bookedMinutes, pauseMinutes, unknownCapacityDays,
  } = input;

  const capacityComplete = unknownCapacityDays === 0;
  const capacityUnknown = availableMinutes <= 0 && unknownCapacityDays > 0;

  const pct = (part: number): number | null =>
    capacityUnknown ? null
      : availableMinutes > 0 ? Math.round((part / availableMinutes) * 100)
        : 0;

  return {
    utilizationPct: pct(wrenchingMinutes),
    bookLoadPct: pct(bookedMinutes),
    idleMinutes: capacityUnknown
      ? null
      : Math.max(0, availableMinutes - wrenchingMinutes - pauseMinutes),
    // Both halves measured, so this survives an unknown capacity unchanged.
    overUnderMinutes: wrenchingMinutes - bookedMinutes,
    capacityComplete,
    capacityUnknown,
  };
}
